import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import type { AvailableDocument, InputDocument, ThreadSender } from "./send";

export function DocumentPicker({ sender, selected, frozen, disabled, onChange }: {
  sender: ThreadSender;
  selected: InputDocument[];
  frozen: boolean;
  disabled: boolean;
  onChange: (documents: InputDocument[]) => Promise<void>;
}) {
  const [choices, setChoices] = useState<AvailableDocument[]>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [choice, setChoice] = useState("");
  const lifetime = useRef<AbortController | undefined>(undefined);
  useEffect(() => () => lifetime.current?.abort(), [sender]);

  async function open() {
    if (disabled || frozen || loading) return;
    lifetime.current?.abort();
    const controller = new AbortController();
    lifetime.current = controller;
    setLoading(true); setError(false); setChoices(undefined); setChoice("");
    try {
      const result = await sender.documents(controller.signal);
      if (!controller.signal.aborted) setChoices(result);
    } catch { if (!controller.signal.aborted) setError(true); }
    finally { if (!controller.signal.aborted) setLoading(false); }
  }

  const available = choices?.filter((item) => !selected.some((ref) => ref.version_id === item.reference.version_id));
  return <div aria-label="Attached documents" className="mt-3 text-sm text-[#52606e]">
    {selected.length ? <ul className="mb-3 space-y-2">
      {selected.map((ref, index) => <li key={ref.version_id} className="flex items-center justify-between gap-3">
        <span className="min-w-0 break-words">{choices?.find((item) => item.reference.artifact_id === ref.artifact_id && item.reference.version_id === ref.version_id)?.title || `Document ${index + 1}`} <span className="text-xs">(saved version)</span></span>
        {!frozen ? <Button variant="ghost" disabled={disabled} aria-label={`Remove document ${index + 1}`} onClick={() => void onChange(selected.filter((_, i) => i !== index))}>Remove</Button> : null}
      </li>)}
    </ul> : null}
    {!frozen ? <>
      {choices ? <div className="flex flex-wrap items-center gap-2">
        {available?.length ? <><label className="sr-only" htmlFor="saved-document">Saved document</label>
          <select id="saved-document" value={choice} disabled={disabled || selected.length >= 32} onChange={(event) => setChoice(event.target.value)} className="min-w-0 flex-1 rounded-lg border border-[#cfe4f1] bg-white px-3 py-2 text-[#20272b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8]">
            <option value="">Choose a document…</option>
            {available.map((item) => <option key={item.reference.version_id} value={item.reference.version_id}>{item.title}</option>)}
          </select>
          <Button variant="ghost" disabled={disabled || !choice || selected.length >= 32} onClick={() => {
            const item = available.find((item) => item.reference.version_id === choice);
            if (item) { setChoice(""); void onChange([...selected, item.reference]); }
          }}>Attach</Button></> : <p>No more saved documents are available for this conversation.</p>}
        <Button variant="ghost" disabled={disabled || loading} onClick={() => void open()}>Reload documents</Button>
      </div> : <Button variant="ghost" disabled={disabled || loading} onClick={() => void open()}>{loading ? "Loading documents…" : "Attach saved document"}</Button>}
      {selected.length >= 32 ? <p className="mt-2">Up to 32 documents can be attached to one message.</p> : null}
      {error ? <p role="alert" className="mt-2">Saved documents couldn't be loaded. Try again.</p> : null}
    </> : null}
  </div>;
}
